import { useState } from 'react';
import { Button, Callout, Flex, Select, Text } from '@dodilio/ui-design-lib';
import { auth } from '../../auth';
import { ServiceRegister } from '../../api';
import type { OrgRole } from '../../api/register.service';

/*
 * Invite teammates right after creating the organization, or skip. Each
 * address becomes its own row as it's typed or pasted (Enter, comma, space),
 * with the role it gets: Member by default, Admin or Owner when chosen.
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ROLES: { value: OrgRole; label: string; hint: string }[] = [
    { value: 'org.member', label: 'Member', hint: 'Works in the organization' },
    { value: 'org.admin', label: 'Admin', hint: 'Manages its resources and people' },
    { value: 'org.owner', label: 'Owner', hint: 'Full control, billing included' },
];

interface Invitee { email: string; role: OrgRole; failed?: boolean }

/** The addresses in typed or pasted text, and what's left over that isn't one. */
export function splitAddresses(text: string): { emails: string[]; rest: string } {
    const parts = text.split(/[\s,;]+/).map((p) => p.trim()).filter(Boolean);
    return { emails: parts.filter((p) => EMAIL.test(p)), rest: parts.filter((p) => !EMAIL.test(p)).join(' ') };
}

export const InviteTeam = ({ organizationId, onDone }: { organizationId: string; onDone: () => void }) => {
    const [people, setPeople] = useState<Invitee[]>([]);
    const [draft, setDraft] = useState('');
    const [busy, setBusy] = useState(false);

    const add = (text: string) => {
        const { emails, rest } = splitAddresses(text);
        setPeople((list) => {
            const known = new Set(list.map((p) => p.email.toLowerCase()));
            return [...list, ...emails.filter((e) => !known.has(e.toLowerCase())).map((email) => ({ email, role: 'org.member' as OrgRole }))];
        });
        setDraft(rest);
    };
    const remove = (email: string) => setPeople((list) => list.filter((p) => p.email !== email));
    const setRole = (email: string, role: OrgRole) => setPeople((list) => list.map((p) => (p.email === email ? { ...p, role } : p)));

    const send = async () => {
        setBusy(true);
        const token = auth.getAccessToken() || '';
        const results = await Promise.allSettled(people.map((p) => ServiceRegister.inviteToOrganization(organizationId, p.email, p.role, token)));
        const left = people.filter((_, i) => results[i].status === 'rejected').map((p) => ({ ...p, failed: true }));
        setBusy(false);
        if (left.length) { setPeople(left); return; }
        onDone();
    };

    const failed = people.filter((p) => p.failed);
    const typedBad = draft.trim() && !/[\s,;]$/.test(draft) && draft.includes('@') && !EMAIL.test(draft.trim());

    return (
        <Flex direction="column" gap="3">
            <Text size="3" weight="medium">Invite your teammates</Text>
            <Text size="2" color="gray">They get an email to join. You can also invite people later from the console.</Text>
            {failed.length > 0 && (
                <Callout.Root size="1" color="tomato" role="alert">
                    <Callout.Text>
                        We couldn&apos;t invite {failed.map((p) => p.email).join(', ')}. Check the {failed.length > 1 ? 'addresses' : 'address'} and try again, or skip and invite later.
                    </Callout.Text>
                </Callout.Root>
            )}

            <div className="inviteBox" onClick={() => document.getElementById('invite-input')?.focus()}>
                <input
                    id="invite-input"
                    className="inviteInput"
                    value={draft}
                    onChange={(e) => {
                        const v = e.target.value;
                        // A separator typed after an address makes it a row.
                        if (/[\s,;]$/.test(v)) add(v); else setDraft(v);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); add(draft); }
                        if (e.key === 'Backspace' && !draft && people.length) remove(people[people.length - 1].email);
                    }}
                    onPaste={(e) => { e.preventDefault(); add(`${draft} ${e.clipboardData.getData('text')}`); }}
                    onBlur={() => add(draft)}
                    placeholder={people.length ? 'Add another email' : 'name@company.com, then Enter'}
                    aria-label="Teammate's email"
                    autoComplete="off"
                    inputMode="email"
                />
            </div>
            {typedBad && <Text size="1" className="field-error" role="alert">That doesn&apos;t look like an email yet.</Text>}

            {people.length > 0 && (
                <ul className="inviteList" aria-label="People to invite">
                    {people.map((p) => (
                        <li key={p.email} className={p.failed ? 'inviteRow inviteRowFailed' : 'inviteRow'}>
                            <span className="inviteAvatar" aria-hidden>{p.email.charAt(0).toUpperCase()}</span>
                            <Text size="2" truncate className="inviteEmail">{p.email}</Text>
                            <Select.Root value={p.role} onValueChange={(v) => setRole(p.email, v as OrgRole)} size="1">
                                <Select.Trigger variant="soft" aria-label={`Role for ${p.email}`} />
                                <Select.Content>
                                    {ROLES.map((r) => (
                                        <Select.Item key={r.value} value={r.value}>
                                            {r.label}
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select.Root>
                            <button type="button" className="inviteRemove" onClick={() => remove(p.email)} aria-label={`Remove ${p.email}`}>×</button>
                        </li>
                    ))}
                </ul>
            )}
            {people.length > 0 && (
                <Text size="1" color="gray">
                    {ROLES.map((r) => `${r.label}: ${r.hint.toLowerCase()}`).join('. ')}.
                </Text>
            )}

            <Button radius="full" size="3" disabled={busy || people.length === 0} loading={busy} onClick={() => void send()}>
                {people.length > 1 ? `Invite ${people.length} people` : 'Send invite'}
            </Button>
            <Button radius="full" size="3" variant="soft" color="gray" disabled={busy} onClick={onDone}>Skip for now</Button>
        </Flex>
    );
};
